(() => {
  if (customElements.get('product-description-tabs')) return;

  class ProductDescriptionTabs extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.source = document.querySelector('[data-lp-product-content-source]') || this.querySelector('[data-product-description-source]');
      this.shell = this.querySelector('[data-product-description-shell]');
      this.tablist = this.querySelector('[data-product-description-tabs]');
      this.panels = this.querySelector('[data-product-description-panels]');
      this.empty = this.querySelector('[data-product-description-empty]');
      this.previousButton = this.querySelector('[data-product-tabs-scroll="previous"]');
      this.nextButton = this.querySelector('[data-product-tabs-scroll="next"]');
      this.tabButtons = [];
      this.panelElements = [];

      this.onClick = this.handleClick.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onScroll = this.updateScrollControls.bind(this);
      this.onResize = this.updateScrollControls.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.tablist?.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      this.build();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.tablist?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }

    build() {
      if (!this.source || !this.shell || !this.tablist || !this.panels) return;

      const sections = this.parseDescription(this.source.innerHTML.trim());
      this.tablist.replaceChildren();
      this.panels.replaceChildren();

      if (!sections.length) {
        this.shell.hidden = true;
        if (this.empty) this.empty.hidden = false;
        return;
      }

      const tabsFragment = document.createDocumentFragment();
      const panelsFragment = document.createDocumentFragment();

      sections.forEach((section, index) => {
        const tabId = `ProductDescriptionTab-${this.dataset.sectionId}-${index + 1}`;
        const panelId = `ProductDescriptionPanel-${this.dataset.sectionId}-${index + 1}`;
        const button = document.createElement('button');
        const icon = document.createElement('span');
        const label = document.createElement('span');
        const panel = document.createElement('div');
        const content = document.createElement('div');

        button.id = tabId;
        button.className = 'product-description-tabs__tab';
        button.type = 'button';
        button.role = 'tab';
        button.tabIndex = index === 0 ? 0 : -1;
        button.setAttribute('aria-selected', String(index === 0));
        button.setAttribute('aria-controls', panelId);
        button.dataset.productDescriptionTab = String(index);

        icon.className = 'product-description-tabs__tab-icon';
        icon.setAttribute('aria-hidden', 'true');
        const iconImage = document.createElement('img');
        iconImage.src = this.dataset[`icon${section.key[0].toUpperCase()}${section.key.slice(1)}`];
        iconImage.alt = '';
        iconImage.width = 40;
        iconImage.height = 40;
        iconImage.decoding = 'async';
        icon.append(iconImage);

        label.className = 'product-description-tabs__tab-label';
        label.textContent = section.title;
        button.append(icon, label);

        panel.id = panelId;
        panel.className = 'product-description-tabs__panel';
        panel.role = 'tabpanel';
        panel.tabIndex = 0;
        panel.setAttribute('aria-labelledby', tabId);
        panel.dataset.productDescriptionPanel = String(index);
        panel.hidden = index !== 0;

        content.className = 'product-description-tabs__content rte';
        if (section.html) {
          content.innerHTML = section.html;
        } else {
          const emptyMessage = document.createElement('p');
          emptyMessage.className = 'product-description-tabs__content-empty';
          emptyMessage.textContent = this.dataset.emptyContent || 'More information will be available soon.';
          content.append(emptyMessage);
        }

        panel.append(content);
        tabsFragment.append(button);
        panelsFragment.append(panel);
      });

      this.tablist.append(tabsFragment);
      this.panels.append(panelsFragment);
      this.tabButtons = Array.from(this.tablist.querySelectorAll('[data-product-description-tab]'));
      this.panelElements = Array.from(this.panels.querySelectorAll('[data-product-description-panel]'));
      this.empty.hidden = true;
      this.shell.hidden = false;
      this.activateTab(0, false);
      window.requestAnimationFrame(() => this.updateScrollControls());
    }

    parseDescription(html) {
      if (!html) return [];

      const parser = new DOMParser();
      const parsedDocument = parser.parseFromString(html, 'text/html');
      const body = parsedDocument.body;
      body.querySelectorAll('script, style, link, meta').forEach((element) => element.remove());

      const headings = Array.from(body.querySelectorAll('h3'));
      const descriptionLabel = this.dataset.descriptionLabel || 'Description';
      const sections = [];

      if (!headings.length) {
        const content = this.cleanFragment(body.cloneNode(true), parsedDocument);
        if (this.hasMeaningfulContent(content)) {
          sections.push({ title: descriptionLabel, html: content.innerHTML.trim(), isIntroduction: true });
        }
        return this.primarySections(sections);
      }

      {
        const introduction = this.cloneRange(parsedDocument, body, null, headings[0]);
        if (this.hasMeaningfulContent(introduction)) {
          sections.push({ title: descriptionLabel, html: introduction.innerHTML.trim(), isIntroduction: true });
        }
      }

      headings.forEach((heading, index) => {
        const nextHeading = headings[index + 1] || null;
        const content = this.cloneRange(parsedDocument, body, heading, nextHeading);
        const title = heading.textContent.replace(/\s+/g, ' ').trim() || `Information ${index + 1}`;
        sections.push({
          title,
          html: this.hasMeaningfulContent(content) ? content.innerHTML.trim() : '',
          isIntroduction: false
        });
      });

      return this.primarySections(sections);
    }

    cloneRange(parsedDocument, body, startAfter, endBefore) {
      const range = parsedDocument.createRange();
      if (startAfter) range.setStartAfter(startAfter);
      else range.setStart(body, 0);

      if (endBefore) range.setEndBefore(endBefore);
      else range.setEnd(body, body.childNodes.length);

      const wrapper = parsedDocument.createElement('div');
      wrapper.append(range.cloneContents());
      return this.cleanFragment(wrapper, parsedDocument);
    }

    cleanFragment(wrapper, parsedDocument) {
      wrapper.querySelectorAll('script, style, link, meta, h3').forEach((element) => element.remove());
      Array.from(wrapper.querySelectorAll('p, div, section')).reverse().forEach((element) => {
        const text = element.textContent.replace(/\s+/g, '').trim();
        const media = element.querySelector('img, video, iframe, table, ul, ol, a[href]');
        if (!text && !media) element.remove();
      });
      return wrapper;
    }

    hasMeaningfulContent(element) {
      if (!element) return false;
      const text = element.textContent.replace(/\s+/g, '').trim();
      return Boolean(text || element.querySelector('img, video, iframe, table, ul, ol, a[href]'));
    }

    primarySections(sections) {
      if (!sections.length) return [];
      const normalized = section => section.title.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, ' ').trim();
      const find = (exact, fallback) => sections.find(s => exact.test(normalized(s))) || sections.find(s => fallback.test(normalized(s)));
      const intro = sections.find(s => s.isIntroduction);
      const description = sections.find(s => !s.isIntroduction && /^(product )?(description|overview|details)$/.test(normalized(s)));
      const benefits = find(/^(key |product |spiritual |traditional )?benefits$/, /\bbenefits?\b/);
      const usage = find(/^(usage guidelines|usage|use and care|wearing guidelines)$/, /^(how to use|how to wear|usage|care instructions|wear and care)/);
      const quality = find(/^quality and certifications?$/, /^(quality|certifications?|authenticity|lab report)/);
      return [
        { key: 'description', title: 'Description', html: [intro?.html, description?.html].filter(Boolean).join('\n'), isIntroduction: true },
        { key: 'benefits', title: 'Benefits', html: benefits?.html || '' },
        { key: 'usage', title: 'Usage Guidelines', html: usage?.html || '' },
        { key: 'quality', title: 'Quality and Certifications', html: quality?.html || '' }
      ];
    }

    handleClick(event) {
      const tab = event.target.closest('[data-product-description-tab]');
      if (tab && this.contains(tab)) {
        this.activateTab(Number(tab.dataset.productDescriptionTab), false);
        return;
      }

      const scrollButton = event.target.closest('[data-product-tabs-scroll]');
      if (!scrollButton || !this.contains(scrollButton) || !this.tablist) return;
      const direction = scrollButton.dataset.productTabsScroll === 'previous' ? -1 : 1;
      this.tablist.scrollBy({ left: direction * Math.max(220, this.tablist.clientWidth * 0.72), behavior: 'smooth' });
    }

    handleKeydown(event) {
      const currentTab = event.target.closest('[data-product-description-tab]');
      if (!currentTab || !this.contains(currentTab)) return;

      const currentIndex = this.tabButtons.indexOf(currentTab);
      let nextIndex = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % this.tabButtons.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + this.tabButtons.length) % this.tabButtons.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabButtons.length - 1;
      if (nextIndex === null) return;

      event.preventDefault();
      this.activateTab(nextIndex, true);
    }

    activateTab(index, focus) {
      if (!this.tabButtons.length || index < 0 || index >= this.tabButtons.length) return;

      this.tabButtons.forEach((button, buttonIndex) => {
        const active = buttonIndex === index;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
      });

      this.panelElements.forEach((panel, panelIndex) => {
        const active = panelIndex === index;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });

      const activeButton = this.tabButtons[index];
      if (focus) activeButton.focus({ preventScroll: true });
      if (this.tablist.scrollWidth > this.tablist.clientWidth + 2) {
        const centeredPosition = activeButton.offsetLeft - ((this.tablist.clientWidth - activeButton.offsetWidth) / 2);
        this.tablist.scrollTo({ left: Math.max(0, centeredPosition), behavior: focus ? 'smooth' : 'auto' });
      }
      this.updateScrollControls();
    }

    updateScrollControls() {
      if (!this.tablist || !this.previousButton || !this.nextButton) return;
      const overflow = this.tablist.scrollWidth > this.tablist.clientWidth + 2;
      this.previousButton.hidden = !overflow;
      this.nextButton.hidden = !overflow;
      if (!overflow) return;

      this.previousButton.disabled = this.tablist.scrollLeft <= 2;
      this.nextButton.disabled = this.tablist.scrollLeft + this.tablist.clientWidth >= this.tablist.scrollWidth - 2;
    }
  }

  customElements.define('product-description-tabs', ProductDescriptionTabs);
})();
